import { useMemo, useState } from 'react'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { transcriptsApi } from '../../api/transcripts'
import { RefusedError } from '../../api/client'
import { useSession } from '../../hooks/useSession'
import { useSpoke } from '../../hooks/useSpoke'
import { formatDate } from '../../lib/format'
import { dueLabel, speakerSlot } from '../../lib/transcript'
import type { LedgerOverview, TakeawayView, TranscriptLedgerCounts, TranscriptSummary } from '../../types/transcripts'
import './transcripts.css'

// Transcripts: the firm's scribe library, every meeting transcript in its project context with a pointer to the
// recording — and, read out of each one, the ledger: what the client asked for and what was promised. The library
// opens on what is owed; each row says how its meeting stands; the transcript page holds the ledger itself.

type Only = 'all' | 'open' | 'overdue' | 'unread'

export function Transcripts() {
  const spoke = useSpoke()
  const navigate = useNavigate()
  const { user } = useSession()
  const [params] = useSearchParams()
  const q = useQuery({ queryKey: ['transcripts', spoke], queryFn: () => transcriptsApi.list(spoke), staleTime: 30_000 })
  const ov = useQuery({ queryKey: ['transcripts-ledger', spoke], queryFn: () => transcriptsApi.overview(spoke), staleTime: 15_000 })
  const doImport = useMutation({ mutationFn: () => transcriptsApi.import(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const readAll = useMutation({ mutationFn: () => transcriptsApi.extract(spoke), onSuccess: (r) => navigate(`/jobs/${encodeURIComponent(r.job.id)}`) })
  const [project, setProject] = useState('')
  const [query, setQuery] = useState('')
  const [only, setOnly] = useState<Only>('all')

  const all = q.data?.transcripts ?? []
  const counts = useMemo(() => new Map((ov.data?.transcripts ?? []).map((c) => [c.transcriptId, c])), [ov.data])
  const rows = useMemo(() => {
    let list = all
    if (project) list = list.filter((t) => t.project === project)
    const term = query.trim().toLowerCase()
    if (term) list = list.filter((t) => t.meeting.toLowerCase().includes(term) || t.project.toLowerCase().includes(term) || t.speakers.some((s) => s.toLowerCase().includes(term)))
    if (only !== 'all') list = list.filter((t) => {
      const c = counts.get(t.transcriptId)
      return only === 'open' ? (c?.open ?? 0) > 0 : only === 'overdue' ? (c?.overdue ?? 0) > 0 : !c?.extractedAt
    })
    return list
  }, [all, project, query, only, counts])
  const isAdmin = user?.roles.includes('scout-admin') ?? false
  const unread = all.filter((t) => !counts.get(t.transcriptId)?.extractedAt).length
  const err = (m: { isError: boolean; error: unknown }) => m.isError ? (m.error instanceof RefusedError ? m.error.refusal.message : (m.error as Error).message) : null

  // the old ?open=<id> deep link from a meeting now lands on the transcript's own page
  const open = params.get('open')
  if (open) return <Navigate to={`/transcripts/${encodeURIComponent(open)}`} replace />

  return (
    <section className="page transcripts">
      <header className="voice-head">
        <div>
          <p className="kicker"><Link to="/landscape">Digital Landscape</Link> · the scribe library{ov.data ? ` · ${ov.data.totals.extracted} of ${ov.data.totals.transcripts} read` : ''}</p>
          <h1>Transcripts</h1>
          <p className="lede">Every meeting transcript in its project context, and the ledger read out of each one: what the client asked for, what was promised, who owes it, and by when. Every line carries the words and the timecode it came from.</p>
        </div>
        <div className="actions">
          {ov.data?.activeJobId && <Link className="button" to={`/jobs/${encodeURIComponent(ov.data.activeJobId)}`}>Watch the read</Link>}
          <button className="button primary" onClick={() => readAll.mutate()} disabled={readAll.isPending || !!ov.data?.activeJobId || unread === 0} title="Reads every transcript not yet read for its requirements and takeaways. Each transcript is a job step you can watch.">
            {readAll.isPending ? 'Starting…' : unread > 0 ? `Read ${unread} unread` : 'All read'}
          </button>
          {isAdmin && <button className="button" onClick={() => doImport.mutate()} disabled={doImport.isPending} title="Pulls every scribed clip from the scribe-split ledger.">{doImport.isPending ? 'Starting…' : 'Import from ledger'}</button>}
        </div>
      </header>
      {err(doImport) && <p className="error">{err(doImport)}</p>}
      {err(readAll) && <p className="error">{err(readAll)}</p>}

      {ov.isSuccess && all.length > 0 && <LibraryGlance overview={ov.data} transcripts={all} onPick={setOnly} />}

      {q.isSuccess && (
        <div className="contact-filters">
          <button className={`chip-filter ${project === '' ? 'on' : ''}`} onClick={() => setProject('')}><strong>{all.length}</strong> <span>all</span></button>
          {(q.data?.projects ?? []).map((p) => (
            <button key={p} className={`chip-filter ${project === p ? 'on' : ''}`} onClick={() => setProject(p)}>
              <strong>{all.filter((t) => t.project === p).length}</strong> <span>{p}</span>
            </button>
          ))}
          <span className="filter-sep" />
          <button className={`chip-filter only ${only === 'open' ? 'on' : ''}`} onClick={() => setOnly(only === 'open' ? 'all' : 'open')}><span>with open takeaways</span></button>
          <button className={`chip-filter only overdue ${only === 'overdue' ? 'on' : ''}`} onClick={() => setOnly(only === 'overdue' ? 'all' : 'overdue')}><span>overdue</span></button>
          <button className={`chip-filter only ${only === 'unread' ? 'on' : ''}`} onClick={() => setOnly(only === 'unread' ? 'all' : 'unread')}><span>not read yet</span></button>
          <input className="contact-search" placeholder="Find a meeting, project, or speaker…" value={query} onChange={(e) => setQuery(e.target.value)} />
        </div>
      )}

      {q.isPending && <p className="muted">Loading…</p>}
      {q.isSuccess && all.length === 0 && (
        <div className="card"><p className="muted">No transcripts yet. "Import from ledger" pulls every scribed meeting from the scribe-split ledger into its project context.</p></div>
      )}
      {q.isSuccess && all.length > 0 && rows.length === 0 && <div className="card"><p className="muted">Nothing matches that filter.</p></div>}

      {rows.length > 0 && (
        <table className="grid transcripts-grid">
          <thead><tr><th>Meeting</th><th>Project</th><th>Date</th><th>Who was in the room</th><th className="num">Requirements</th><th>Takeaways</th><th>Read</th><th>Recording</th></tr></thead>
          <tbody>
            {rows.map((t) => <Row key={t.transcriptId} t={t} c={counts.get(t.transcriptId)} onOpen={() => navigate(`/transcripts/${encodeURIComponent(t.transcriptId)}`)} />)}
          </tbody>
        </table>
      )}
    </section>
  )
}

function Row({ t, c, onOpen }: { t: TranscriptSummary; c: TranscriptLedgerCounts | undefined; onOpen: () => void }) {
  const overdue = c?.overdue ?? 0
  return (
    <tr className={`transcript-row${overdue > 0 ? ' late' : ''}${c?.extractedAt ? '' : ' unread'}`} onClick={onOpen} tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') onOpen() }}>
      <td><strong>{t.meeting}</strong><span className="muted small">{t.clipName} · {t.wordCount.toLocaleString()} words</span></td>
      <td>{t.project}</td>
      <td>{formatDate(t.date)}</td>
      <td className="who-cell">{t.speakers.slice(0, 4).map((s) => <span key={s} className="speaker-chip" style={{ ['--speaker' as string]: `var(--speaker-${speakerSlot(t.speakers, s)})` }}><i />{s}</span>)}{t.speakers.length > 4 && <span className="muted small">+{t.speakers.length - 4}</span>}</td>
      <td className="num">{c?.requirements ? <span className="ledger-n">{c.requirements}</span> : <span className="muted">—</span>}</td>
      <td className="takeaways-cell">
        {c && c.takeaways > 0 ? (
          <>
            <span className="owed ours" title="open, we owe"><strong>{c.oursOpen}</strong> ours</span>
            <span className="owed theirs" title="open, they owe"><strong>{c.theirsOpen}</strong> theirs</span>
            {overdue > 0 && <span className="tag late">{overdue} overdue</span>}
            {c.open === 0 && <span className="tag done">all closed</span>}
          </>
        ) : <span className="muted">—</span>}
      </td>
      <td>{c?.extractedAt ? <span className="tag read">read {formatDate(c.extractedAt)}</span> : <span className="tag unread">not read</span>}</td>
      <td>{t.hasRecording ? <span className="tag sig low">video</span> : <span className="muted small">—</span>}</td>
    </tr>
  )
}

/** The library at a glance: what is owed, the soonest promises, and the numbers. */
function LibraryGlance({ overview, transcripts, onPick }: { overview: LedgerOverview; transcripts: TranscriptSummary[]; onPick: (o: Only) => void }) {
  const t = overview.totals
  const meetings = useMemo(() => new Map(transcripts.map((x) => [x.transcriptId, x])), [transcripts])
  const ours = t.oursOwed, theirs = t.theirsOwed, open = ours + theirs
  const share = open ? Math.round((ours / open) * 100) : 50
  return (
    <section className="glance ledger-glance">
      <div className="glance-owed">
        <p className="kicker">What is owed</p>
        <p className="glance-big"><strong>{t.openTakeaways}</strong><span>open takeaways across {t.extracted} meetings read</span></p>
        <div className="owed-bar" role="img" aria-label={`${ours} we owe, ${theirs} they owe`}>
          <span className="owed-ours" style={{ width: `${share}%` }} />
          <span className="owed-theirs" style={{ width: `${100 - share}%` }} />
        </div>
        <p className="owed-legend"><span><i className="dot ours" />{ours} we owe</span><span><i className="dot theirs" />{theirs} they owe</span></p>
        <div className="glance-callouts">
          {t.overdue > 0 && <button className="callout danger" onClick={() => onPick('overdue')}><strong>{t.overdue}</strong> past the date promised</button>}
          {t.unassigned > 0 && <button className="callout warn" onClick={() => onPick('open')}><strong>{t.unassigned}</strong> of ours with no owner</button>}
          {t.dueThisWeek > 0 && <button className="callout ok" onClick={() => onPick('open')}><strong>{t.dueThisWeek}</strong> due in the next 7 days</button>}
          {t.openTakeaways === 0 && t.extracted > 0 && <span className="callout ok"><strong>0</strong> open — nothing owed</span>}
          {t.extracted === 0 && <span className="callout"><strong>0</strong> read — press Read to build the ledger</span>}
        </div>
      </div>

      <div className="glance-soonest">
        <p className="kicker">Soonest promised</p>
        {overview.soonest.length === 0 && <p className="muted">No promised dates on the ledger yet.</p>}
        <ul>
          {overview.soonest.map((v) => <SoonestRow key={v.takeaway.takeawayId} v={v} meeting={meetings.get(v.takeaway.transcriptId)} />)}
        </ul>
      </div>

      <div className="glance-stats">
        <div className="glance-stat"><strong>{t.transcripts}</strong><span>transcripts</span></div>
        <div className="glance-stat"><strong>{t.extracted}</strong><span>read for their ledger</span></div>
        <div className="glance-stat"><strong>{t.requirements}</strong><span>requirements</span></div>
        <div className="glance-stat"><strong>{t.openTakeaways}</strong><span>open takeaways</span></div>
        <div className={`glance-stat${t.overdue > 0 ? ' hot' : ''}`}><strong>{t.overdue}</strong><span>overdue</span></div>
        <div className="glance-stat"><strong>{t.dueThisWeek}</strong><span>due this week</span></div>
        <div className="glance-stat ours"><strong>{t.oursOwed}</strong><span>we owe</span></div>
        <div className="glance-stat theirs"><strong>{t.theirsOwed}</strong><span>they owe</span></div>
      </div>
    </section>
  )
}

function SoonestRow({ v, meeting }: { v: TakeawayView; meeting: TranscriptSummary | undefined }) {
  const t = v.takeaway
  return (
    <li className={`soonest ${v.due.state}`}>
      <Link to={`/transcripts/${encodeURIComponent(t.transcriptId)}`} className="soonest-text">{t.text}</Link>
      <span className="soonest-meta">
        <span className={`owed ${t.kind}`}>{t.kind === 'ours' ? 'we owe' : 'they owe'}</span>
        {meeting && <span className="muted">{meeting.meeting}</span>}
        <span className={`due ${v.due.state}`}>{dueLabel(v.due, t.dueOn, formatDate)}</span>
      </span>
    </li>
  )
}
